// mbscan manager-client の同名部品と共通。片側だけで変えない
import { isSome } from '@mbfab/shared'
import { useId } from 'react'

import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/form.md

// 補助的な設定がページ・ダイアログの両方に収まるよう、カードよりコンパクトにする。
// キーボード操作と一括無効化をブラウザ標準に任せるため、ネイティブのラジオを使う。

export type SegmentedControlOption<Value extends string> = {
  value: Value
  label: string
  testId?: string
}

export function SegmentedControl<Value extends string>({
  legend,
  description,
  name,
  options,
  value,
  onChange,
  disabled = false,
  testId,
  className,
}: {
  legend: string
  description?: string
  // 同じ画面に複数置いてもラジオのグループが混ざらないよう、呼び出し側で一意な name を渡す
  name: string
  options: ReadonlyArray<SegmentedControlOption<Value>>
  value: Value
  onChange: (value: Value) => void
  disabled?: boolean
  testId?: string
  className?: string
}) {
  const descriptionId = useId()

  return (
    <fieldset
      disabled={disabled}
      aria-describedby={isSome(description) ? descriptionId : undefined}
      data-testid={testId}
      data-disabled={disabled ? '' : undefined}
      className={cn('flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5', className)}
    >
      <legend className="float-left mr-3 text-sm font-medium text-foreground">{legend}</legend>
      <div
        className={cn(
          'inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5',
          disabled && 'opacity-60',
        )}
      >
        {options.map((option) => {
          const checked = option.value === value
          return (
            <label
              key={option.value}
              className={cn(
                'relative inline-flex min-h-8 items-center rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors',
                'has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-ring/50',
                checked ? 'bg-card text-foreground shadow-xs ring-1 ring-border' : 'text-muted-foreground',
                disabled ? 'cursor-not-allowed' : checked ? 'cursor-default' : 'cursor-pointer hover:text-foreground',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                data-testid={option.testId}
                className="sr-only"
                onChange={() => {
                  onChange(option.value)
                }}
              />
              {option.label}
            </label>
          )
        })}
      </div>
      {isSome(description) && (
        <p id={descriptionId} className="basis-full text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}
    </fieldset>
  )
}
